import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import mainlogogray from "./images/mainlogogray.png";

const styles = StyleSheet.create({
  page: {
    padding: 12, // more padding for 80mm
    fontSize: 9.5, // slightly larger text
    // fontFamily: "monospace",
  },
  center: {
    textAlign: "center",
  },
  bold: {
    fontWeight: "bold",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginVertical: 6,
    borderStyle: "dashed", // dashed looks better on receipts
  },
  rowBetween: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  // New summary container for right-aligned totals
  summaryContainer: {
    marginVertical: 4,
    paddingHorizontal: 4,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 3,
  },
  totalRow: {
    marginTop: 2,
    fontSize: 11,
  },
  discountRow: {
    color: "#d9534f",
  },
  balanceRow: {
    marginTop: 4,
  },
  balanceDue: {
    fontWeight: "bold",
    color: "#d9534f",
  },
  paidFull: {
    fontWeight: "bold",
    color: "#5cb85c",
  },
  footer: {
    marginVertical: 8,
    paddingVertical: 4,
  },
});

export default function InvoiceReceiptPdf({
  shopEmail = "info@prosepservices.com",
  shopPhone = "0244235128 / 0302790300",
  invoiceData,
  customerData,
  items = [],
  total = 0,
  discount = 0,
  totalPaid = 0,
  remainingBalance = 0,
}) {
  // Calculate subtotal from items
  const subtotal = items.reduce((sum, item) => {
    const qty = Number(item.quantity || 0);
    const price = Number(item.unit_price || 0);
    return sum + qty * price;
  }, 0);

  // Calculate tax (if applicable - currently 0 in your WhatsApp format)
  const tax = 0;

  return (
    <Document>
      {/* 80mm thermal receipt - full width utilization */}
      <Page size={[226, 1000]} style={styles.page}>
        {/* HEADER - centered */}
        <Image
          src={mainlogogray}
          alt="Shop Logo"
          style={{
            width: 160,
            height: 80,
            alignSelf: "center",
            marginBottom: 8,
          }}
        />
        <Text style={styles.center}>Tel: {shopEmail}</Text>
        <Text style={styles.center}>Tel: {shopPhone}</Text>

        <View style={styles.divider} />

        {/* META INFO - two column layout possible */}
        <View style={styles.rowBetween}>
          <Text>
            <Text style={styles.bold}>Invoice#:</Text>{" "}
            {invoiceData?.attributes?.field_order_number}
          </Text>
          <Text>
            <Text style={styles.bold}>Date:</Text>{" "}
            {new Date(invoiceData?.attributes?.created).toLocaleString("en-GH")}
          </Text>
        </View>

        <Text style={{ marginTop: 4 }}>
          <Text style={styles.bold}>Customer:</Text>{" "}
          {customerData?.name || "Walk-in"}
        </Text>

        <View style={styles.divider} />

        {/* ITEMS HEADER - better spacing for 80mm */}
        <View style={[styles.rowBetween, { marginBottom: 4 }]}>
          <Text style={[styles.bold, { width: "60%" }]}>Item</Text>
          <Text style={[styles.bold, { width: "20%", textAlign: "right" }]}>
            Qty
          </Text>
          <Text style={[styles.bold, { width: "20%", textAlign: "right" }]}>
            Total
          </Text>
        </View>

        <View style={styles.divider} />

        {/* ITEMS - detailed for 80mm */}
        {items.map((item, index) => (
          <View key={index} style={{ marginBottom: 6 }}>
            <Text style={[styles.bold, { fontSize: 10 }]}>
              {item.product_title}
            </Text>
            <View style={styles.rowBetween}>
              <Text style={{ width: "60%" }}>
                {item.quantity} x GHC{Number(item.unit_price).toFixed(2)}
              </Text>
              <Text style={{ width: "40%", textAlign: "right" }}>
                GHC{Number(item.line_total).toFixed(2)}
              </Text>
            </View>
          </View>
        ))}

        <View style={styles.divider} />

        {/* PAYMENT SUMMARY - aligned right for better readability */}
        <View style={styles.summaryContainer}>
          <View style={styles.summaryRow}>
            <Text>Items Subtotal:</Text>
            <Text>GHC{subtotal.toFixed(2)}</Text>
          </View>

          {discount > 0 && (
            <>
              <View style={[styles.summaryRow, styles.discountRow]}>
                <Text>Discount:</Text>
                <Text>- GHC{discount.toFixed(2)}</Text>
              </View>
              <View style={[styles.summaryRow, { marginTop: -2 }]}>
                <Text style={{ fontSize: 8, paddingLeft: 10 }}>
                  ({((discount / subtotal) * 100).toFixed(1)}% off)
                </Text>
                <Text></Text>
              </View>
            </>
          )}

          {/* <View style={styles.summaryRow}>
            <Text>Tax (GST/VAT):</Text>
            <Text>GHC{tax.toFixed(2)}</Text>
          </View> */}

          <View style={[styles.divider, { marginVertical: 6 }]} />

          <View style={[styles.summaryRow, styles.totalRow]}>
            <Text style={styles.bold}>TOTAL AMOUNT:</Text>
            <Text style={styles.bold}>GHC{total.toFixed(2)}</Text>
          </View>

          <View style={styles.summaryRow}>
            <Text>Total Paid:</Text>
            <Text>GHC{totalPaid.toFixed(2)}</Text>
          </View>

          <View style={[styles.summaryRow, styles.balanceRow]}>
            <Text>Balance Due:</Text>
            <Text
              style={remainingBalance > 0 ? styles.balanceDue : styles.paidFull}
            >
              GHC{remainingBalance.toFixed(2)}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* FOOTER - with decorative line for 80mm */}
        <View style={styles.footer}>
          <Text style={[styles.center, { fontSize: 10, marginBottom: 4 }]}>
            Thank you for your business
          </Text>
          <Text style={styles.center}>We hope to see you again soon!</Text>
          <Text
            style={[
              styles.center,
              { fontSize: 8, marginTop: 6, color: "#666" },
            ]}
          >
            Goods sold are not returnable
          </Text>
        </View>

        {/* Optional: QR Code / Barcode space */}
        <View style={[styles.divider, { marginTop: 8 }]} />
        <Text style={[styles.center, { fontSize: 8, marginTop: 4 }]}>
          {invoiceData?.attributes?.field_order_number}
        </Text>
      </Page>
    </Document>
  );
}
